import { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';

// ----------------------------------------------------------------------

/**
 * Checks whether an error is caused by a network disconnection or fetch failure.
 *
 * @param {any} error - The caught error object, string, or RTK Query error
 * @returns {boolean}
 */
export function isOfflineError(error) {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return true;
  }

  if (!error) return false;

  if (error?.status === 'FETCH_ERROR') return true;

  if (error instanceof TypeError || error?.name === 'TypeError') return true;

  const checkText = (text) => {
    if (typeof text !== 'string') return false;
    const lower = text.toLowerCase();
    return (
      lower.includes('fetch') ||
      lower.includes('failed to fetch') ||
      lower.includes('lost connection') ||
      lower.includes('network') ||
      lower.includes('connection') ||
      lower.includes('offline')
    );
  };

  if (typeof error === 'string') return checkText(error);
  if (checkText(error?.error)) return true;
  if (checkText(error?.message)) return true;
  if (checkText(error?.data?.message)) return true;
  if (checkText(error?.data?.error)) return true;

  return false;
}

/**
 * Extracts a user-friendly error message, returning 'Lost connection' for network failures.
 *
 * @param {any} error - The caught error
 * @param {string} [defaultMessage='Something went wrong'] - Fallback message
 * @returns {string}
 */
export function getNetworkErrorMessage(error, defaultMessage = 'Something went wrong') {
  if (isOfflineError(error)) {
    return 'Lost connection';
  }

  if (typeof error === 'string') return error;

  if (error?.data?.message) {
    const msg = error.data.message;
    if (Array.isArray(msg)) return msg.join(', ');
    return error?.status === 401 ? 'Session expired' : String(msg);
  }

  if (error?.data?.error) {
    return typeof error.data.error === 'string' ? error.data.error : defaultMessage;
  }

  if (error?.error) {
    return typeof error.error === 'string' ? error.error : defaultMessage;
  }

  if (error?.message) {
    return String(error.message);
  }

  return defaultMessage;
}

// ----------------------------------------------------------------------

/**
 * Custom hook to monitor online/offline status and provide reusable network error handling.
 *
 * @returns {{
 *   isOffline: boolean,
 *   isOnline: boolean,
 *   isOfflineError: (error: any) => boolean,
 *   getErrorMessage: (error: any, defaultMessage?: string) => string,
 *   handleNetworkError: (error: any, defaultMessage?: string) => { isOffline: boolean, message: string }
 * }}
 */
export function useOffline() {
  const [isOffline, setIsOffline] = useState(() => (
    typeof navigator !== 'undefined' ? !navigator.onLine : false
  ));

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const checkIsOfflineError = useCallback((error) => {
    if (isOffline) return true;
    return isOfflineError(error);
  }, [isOffline]);

  const getErrorMessage = useCallback((error, defaultMessage) => {
    if (isOffline || isOfflineError(error)) {
      return 'Lost connection';
    }
    return getNetworkErrorMessage(error, defaultMessage);
  }, [isOffline]);

  const handleNetworkError = useCallback((error, defaultMessage = 'An unexpected error occurred') => {
    const isConn = isOffline || isOfflineError(error);
    const message = isConn ? 'Lost connection' : getNetworkErrorMessage(error, defaultMessage);

    enqueueSnackbar(message, { variant: 'error' });

    return {
      isOffline: isConn,
      message,
    };
  }, [isOffline, enqueueSnackbar]);

  return {
    isOffline,
    isOnline: !isOffline,
    isOfflineError: checkIsOfflineError,
    getErrorMessage,
    handleNetworkError,
  };
}

export default useOffline;
